import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Mail, Search } from 'lucide-react';
import { toast } from 'react-toastify';
import { Button } from '../components/common/Button';
import { useAdminStore } from '../stores/adminStore';
import { AdminUserLookupResult } from '../types/admin_user_lookup';
import GenerateAuthLinkModal from '../components/admin/modals/GenerateAuthLinkModal';

const AdminUserLookup: React.FC = () => {
  const { t } = useTranslation(['admin', 'common']);
  const { lookupUserByEmail } = useAdminStore();

  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [result, setResult] = useState<AdminUserLookupResult | null>(null);
  const [showGenerateLinkModal, setShowGenerateLinkModal] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || loading) return;

    setLoading(true);
    setSearched(false);
    setResult(null);
    setShowGenerateLinkModal(false);
    try {
      const data = await lookupUserByEmail(email.trim());
      setResult(data);
      setSearched(true);
    } catch (err) {
      toast.error(
        (err as Error).message ||
          t('userLookup.error', {
            ns: 'admin',
            defaultValue: 'Could not look up this user.',
          })
      );
    } finally {
      setLoading(false);
    }
  };

  const username = result ? `${result.first_name ?? ''} ${result.last_name ?? ''}`.trim() : '';
  const isConfirmed = Boolean(result?.email_confirmed_at);

  return (
    <>
      <div className="w-full pt-4 pb-5 flex flex-col md:w-2/3 md:px-1 md:pr-8 md:pb-8">
        <h1 className="text-5xl font-bold hyphens-auto">
          {t('userLookup.title', { ns: 'admin', defaultValue: 'Find a user' })}
        </h1>
      </div>

      <div className="overflow-hidden rounded-2xl bg-white border border-gray-300 shadow-sm transition-all p-6 md:p-8">
        <form onSubmit={handleSubmit} className="flex flex-col gap-2 md:flex-row md:items-end">
          <div className="flex-1">
            <label
              htmlFor="user-lookup-email"
              className="text-sm font-semibold text-gray-700 block mb-1"
            >
              {t('userLookup.emailLabel', { ns: 'admin', defaultValue: 'Email' })}
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="h-5 w-5 text-secondary-light" />
              </div>
              <input
                id="user-lookup-email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t('userLookup.emailPlaceholder', {
                  ns: 'admin',
                  defaultValue: 'name@example.com',
                })}
                className="block w-full pl-10 pr-3 py-3 rounded-md sm:text-sm border border-gray-300 bg-secondary-light"
                required
              />
            </div>
          </div>

          <Button type="submit" disabled={loading} className="px-6 flex items-center gap-2">
            <Search size={16} />
            {loading
              ? t('loading', { ns: 'common', defaultValue: 'Chargement...' })
              : t('userLookup.searchButton', { ns: 'admin', defaultValue: 'Search' })}
          </Button>
        </form>

        {searched && !result && (
          <div className="mt-6 text-sm text-gray-600">
            {t('userLookup.notFound', {
              ns: 'admin',
              defaultValue: 'No user found with this email.',
            })}
          </div>
        )}

        {result && (
          <div className="mt-6 rounded-2xl border border-gray-200 p-5">
            <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
              <div>
                {username && <div className="font-semibold text-gray-900">{username}</div>}
                <div className="text-sm text-gray-600">{result.email}</div>
              </div>

              <span
                className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${
                  isConfirmed ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-800'
                }`}
              >
                {isConfirmed
                  ? t('userLookup.confirmed', { ns: 'admin', defaultValue: 'Confirmed' })
                  : t('userLookup.unconfirmed', { ns: 'admin', defaultValue: 'Unconfirmed' })}
              </span>
            </div>

            {result.role !== 'administrator' && (
              <div className="mt-5 flex justify-end">
                <Button
                  variant="primary"
                  type="button"
                  onClick={() => setShowGenerateLinkModal(true)}
                  className="px-6"
                >
                  {t('userLookup.generateLinkButton', {
                    ns: 'admin',
                    defaultValue: 'Generate link',
                  })}
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {showGenerateLinkModal && result && (
        <GenerateAuthLinkModal
          profile={{
            id: result.id,
            first_name: result.first_name ?? undefined,
            last_name: result.last_name ?? undefined,
          }}
          onClose={() => setShowGenerateLinkModal(false)}
        />
      )}
    </>
  );
};

export default AdminUserLookup;
